/**
 * Per-calculator dwell-time copy: "How to use this calculator" steps,
 * "Understanding your results" takeaways, next-steps, and glossary
 * overrides. Keeps the individual calculator pages small and lets
 * editorial tweaks happen in one file.
 */
import { COMMON_GLOSSARY } from "../components/ResultExplainer";

const C = COMMON_GLOSSARY;

export const CALC_COPY = {
  // ---------------- 2026 Federal Tax Bracket ----------------
  "tax-bracket-2026": {
    howToSteps: [
      {
        title: "Pick your filing status",
        body: "Married Filing Jointly brackets are roughly double the Single brackets. Head of Household sits between the two — pick wrong and every number below is off.",
      },
      {
        title: "Enter gross annual income",
        body: "Use your total pre-tax income: salary + bonuses + self-employment + interest + dividends. Don't subtract 401(k) contributions — the calculator handles the standard deduction automatically.",
      },
      {
        title: "Add dependents and itemized",
        body: "Dependents under 17 unlock the $2,200 Child Tax Credit (2026). Enter itemized deductions (mortgage interest, charity, SALT up to $40k) only if you expect them to exceed the $16,100 standard deduction.",
      },
      {
        title: "Compare 2025 vs 2026",
        body: "You'll see side-by-side numbers plus a bottom-line difference. Most middle-income earners save money in 2026 thanks to OBBBA's extra inflation bump on the 10% and 12% brackets.",
      },
    ],
    takeaways: [
      "Your **marginal rate** is the bracket applied to your NEXT dollar of income — not your whole paycheck. A 22% marginal rate doesn't mean you pay 22% on everything.",
      "The **effective rate** is what you actually pay as a percentage of gross income. For most middle-income filers, it's roughly half the marginal rate because of the standard deduction and lower brackets stacking underneath.",
      "**OBBBA's bonus inflation bump** on the 10% and 12% brackets means a typical $55k single filer pays a few hundred dollars less in 2026 than they would have under standard inflation adjustments alone.",
      "This calculator shows federal income tax only. Your actual paycheck deductions also include FICA (7.65%), state income tax, and any elective deductions (401(k), health premiums).",
    ],
    nextSteps: [
      "If your marginal rate is 22% or higher, consider pre-tax 401(k) contributions — every $1,000 contributed saves $220+ in federal tax.",
      "Compare with your [[State Calculator|/states]] to see your full combined tax picture.",
      "If you expect income to jump brackets soon (promotion, side-hustle), model both scenarios now and plan Roth conversions or timing of bonuses.",
    ],
    glossary: [
      C.filing_status,
      C.effective_rate,
      C.marginal_rate,
      C.take_home,
      C.standard_deduction,
    ],
  },

  // ---------------- Standard Deduction ----------------
  "standard-deduction-2026": {
    howToSteps: [
      {
        title: "Choose your filing status",
        body: "The 2026 standard deduction is $16,100 (single), $32,200 (MFJ), $24,150 (Head of Household). OBBBA kept TCJA's near-doubled deduction permanent and layered on the usual inflation bump.",
      },
      {
        title: "Enter age and blindness info",
        body: "Age 65+ or legally blind gets an EXTRA standard deduction on top of the base amount — $1,600 per qualifier for MFJ, $2,000 for unmarried filers in 2026.",
      },
      {
        title: "See your total deduction",
        body: "The calculator sums your base standard deduction plus any additional age/blindness amounts. This is what you subtract from AGI before hitting the brackets.",
      },
      {
        title: "Compare to itemizing",
        body: "Only itemize (mortgage interest + charity + up to $40k SALT) if the total clearly exceeds your standard deduction. For most households post-OBBBA, the standard deduction wins.",
      },
    ],
    takeaways: [
      "After OBBBA, about **90% of filers take the standard deduction** — the near-doubled amount plus the $40k SALT cap mean itemizing only pays off for high-income homeowners or big charitable donors.",
      "The **age 65+ additional deduction** stacks with the new $6,000 [[Senior Deduction|senior-deduction-2026]] for 2025-2028. Combined, a single 65-year-old can deduct $22,100+ before brackets apply.",
      "The standard deduction is subtracted BEFORE your tax bracket is calculated — it effectively shields that dollar amount from ALL federal income tax, not just your top bracket rate.",
    ],
    nextSteps: [
      "Quickly list your itemizable deductions (SALT, mortgage interest, charitable donations) — if they're under the standard deduction, skip the work of tracking receipts this year.",
      "If you're 65+, also run the [[Senior $6k Deduction|senior-deduction-2026]] — it's ON TOP of standard deduction and is new under OBBBA.",
    ],
    glossary: [C.filing_status, C.standard_deduction, C.agi],
  },

  // ---------------- Child Tax Credit ----------------
  "child-tax-credit-2026": {
    howToSteps: [
      {
        title: "Count qualifying children",
        body: "Children must be under 17, your dependent, a US citizen/national/resident alien, and have lived with you >6 months. Each qualifies for up to $2,200 in 2026 under OBBBA.",
      },
      {
        title: "Enter filing status + AGI",
        body: "Phase-out starts at $200k AGI (single) / $400k (MFJ). Above that, credit reduces by $50 per $1,000 of income over the threshold.",
      },
      {
        title: "Calculate",
        body: "See your total CTC and how much is refundable ($1,700/child in 2026) versus nonrefundable (reduces tax owed only).",
      },
      {
        title: "Compare 2025 vs 2026",
        body: "OBBBA raised the per-child amount from $2,000 to $2,200 and indexed it to inflation going forward — a permanent, growing benefit.",
      },
    ],
    takeaways: [
      "The **Child Tax Credit is a credit, not a deduction** — it subtracts directly from tax owed, dollar for dollar. Much more valuable than a $2,200 deduction, which would only save you your marginal rate (say 22%) × $2,200.",
      "**$1,700 of the $2,200 is refundable** in 2026 — meaning even if you owe $0 in tax, you get the $1,700 as a cash refund. The other $500 can only reduce tax owed to zero.",
      "The phase-out threshold ($200k/$400k) is MUCH more generous than the pre-TCJA $75k/$110k — most middle-class and even upper-middle-class families receive the full credit.",
    ],
    nextSteps: [
      "Verify each child has a valid Social Security Number issued before the tax return due date — required for the refundable portion.",
      "If AGI is close to the phase-out threshold, consider traditional 401(k) contributions to lower AGI and preserve the full credit.",
      "Also run the [[2026 Tax Bracket|tax-bracket-2026]] calculator with dependents entered — it automatically nets the CTC against your total federal tax bill.",
    ],
    glossary: [C.filing_status, C.agi],
  },

  // ---------------- Capital Gains ----------------
  "capital-gains-2026": {
    howToSteps: [
      {
        title: "Enter long-term capital gain",
        body: "Only gains on assets held >12 months qualify for the preferential 0% / 15% / 20% rates. Short-term gains (held ≤12 months) are taxed as ordinary income — use the bracket calculator instead.",
      },
      {
        title: "Add your ordinary income",
        body: "LTCG rates are determined by your taxable ordinary income. The 0% bracket covers gains if your total taxable income stays below $48,350 (single) / $96,700 (MFJ) in 2026.",
      },
      {
        title: "Pick filing status",
        body: "MFJ thresholds are roughly double Single. Head of Household falls between the two — critical for retirees filing singly.",
      },
      {
        title: "See the tax",
        body: "You'll see the portion taxed at each rate (0/15/20%) plus any Net Investment Income Tax (NIIT) of 3.8% if MAGI exceeds $200k/$250k.",
      },
    ],
    takeaways: [
      "The **0% long-term capital gains bracket** is one of the biggest tax-planning opportunities in the code — retirees with low ordinary income can harvest gains tax-free up to the threshold.",
      "LTCG rates **stack on top** of your ordinary income. A $50k salary + $30k long-term gain puts you in the 15% LTCG bracket even though your salary alone would've hit the 0% threshold.",
      "**NIIT (3.8%)** hits investment income (interest, dividends, capital gains, rental) above $200k MAGI single / $250k MFJ — so effective LTCG rate becomes 18.8% or 23.8% at the top.",
    ],
    nextSteps: [
      "Plan gain 'harvesting' in low-income years (gap years between jobs, early retirement) to capture 0% rate on gains.",
      "Consider tax-loss harvesting in taxable accounts to offset realized gains — $3,000/year of excess losses can also offset ordinary income.",
    ],
    glossary: [C.filing_status, C.magi, C.marginal_rate],
  },

  // ---------------- SALT Cap ----------------
  "salt-deduction-2026": {
    howToSteps: [
      {
        title: "Enter state + local income tax",
        body: "Total paid for the year — pull from last year's W-2 box 17 + any estimated state tax payments. Residents of no-income-tax states (TX, FL, WA, etc.) enter $0 and use sales tax deduction separately.",
      },
      {
        title: "Add property taxes",
        body: "Annual property tax on your primary residence (and second home if applicable). Use your most recent property tax bill — a January 2026 bill counts for tax year 2025, etc.",
      },
      {
        title: "Set marginal rate and MAGI",
        body: "Marginal rate = your top federal bracket (22%, 24%, 32%, 35%, 37%). MAGI is roughly AGI — phase-out starts at $500k, reduction ~30% of excess.",
      },
      {
        title: "See your deduction & benefit",
        body: "The calculator caps total SALT at $40,000 (2026), applies the high-income phase-out, and shows actual federal tax savings (deduction × marginal rate).",
      },
    ],
    takeaways: [
      "**OBBBA quadrupled the TCJA cap** from $10k to $40k for 2026, with annual 1% increases through 2029. This is the biggest itemization story of OBBBA — blue-state homeowners suddenly have a reason to itemize again.",
      "The **$40k cap is reduced above $500k MAGI** by approximately 30% of the excess. Floor is $10k — even at $2M MAGI you still get the original TCJA cap.",
      "SALT is an **itemized** deduction — you only benefit if your total itemized (SALT + mortgage interest + charity) exceeds your standard deduction ($16,100 single / $32,200 MFJ in 2026).",
      "The **Alternative Minimum Tax** still exists under pre-TCJA rules from 2026 — large SALT deductions can trigger AMT for some filers. Check with a CPA if your SALT > $20k and income > $200k.",
    ],
    nextSteps: [
      "If your state income + property tax exceeds $10k but is under $40k, OBBBA just unlocked thousands in extra federal deductions — review last year's return.",
      "For MAGI between $500k-$600k, timing strategies (bunching charitable gifts, deferring bonuses) may preserve more of the $40k cap.",
      "Pair this with your [[State Calculator|/states]] to see how state income tax (which is what's being capped here) flows back into your overall combined burden.",
    ],
    glossary: [
      C.magi,
      C.standard_deduction,
      C.marginal_rate,
      {
        term: "SALT",
        definition:
          "State And Local Taxes — the combined deduction for state/local income tax (or sales tax) plus property tax, capped at $40,000 under OBBBA for 2026.",
      },
    ],
  },

  // ---------------- QBI ----------------
  "qbi-deduction-2026": {
    howToSteps: [
      {
        title: "Enter qualified business income",
        body: "Net income from a pass-through business: sole prop (Schedule C), partnership (K-1 ordinary), S-corp (K-1 ordinary), or LLC taxed as one of the above. NOT W-2 wages, capital gains, or investment income.",
      },
      {
        title: "Enter taxable income",
        body: "Your TOTAL taxable income (after standard/itemized deduction). This determines whether you're above or below the threshold where SSTB limits and W-2-wage limits kick in.",
      },
      {
        title: "Pick filing status",
        body: "2026 thresholds: $201,800 (single) / $403,550 (MFJ) — above these, SSTB businesses (law, health, consulting, etc.) start phasing out; other businesses hit W-2 wage tests.",
      },
      {
        title: "See your 20% deduction",
        body: "The calculator applies the lesser-of rules (20% of QBI vs 20% of taxable income minus capital gains) and any applicable phase-outs.",
      },
    ],
    takeaways: [
      "**Section 199A is permanent under OBBBA** — no more sunset anxiety. The 20% deduction is now baked into the code indefinitely.",
      "A 20% QBI deduction on $100k of pass-through income = $20k deduction = roughly $4,800 in federal tax savings at a 24% marginal rate. Huge for small business owners.",
      "**SSTBs (Specified Service Trades/Businesses)** — law firms, medical practices, consulting, financial services — phase out ENTIRELY above $451,800 single / $603,550 MFJ (2026, OBBBA-expanded thresholds).",
      "Below the threshold, EVERY pass-through business type qualifies, including SSTBs. The 'wage and UBIA' limitation only applies above the threshold.",
    ],
    nextSteps: [
      "If your business is close to the SSTB phase-out threshold, consider S-corp reasonable salary, retirement contributions (SEP/Solo 401(k)), or income deferral to stay below.",
      "Rental real estate can qualify for QBI if it meets the 250-hour 'trade or business' safe harbor — worth pursuing if you own multiple rentals.",
      "C-corp election is rarely better than pass-through + 20% QBI once you factor in double taxation — but the math flips at very high income levels.",
    ],
    glossary: [
      C.filing_status,
      {
        term: "QBI",
        definition:
          "Qualified Business Income — net income from a pass-through business (Schedule C, partnership, S-corp, rental). Excludes W-2 wages, capital gains, and investment income.",
      },
      {
        term: "SSTB",
        definition:
          "Specified Service Trade or Business — law, health, consulting, financial services, accounting, performing arts, athletics, investment management. These face stricter QBI phase-outs above the income threshold.",
      },
    ],
  },

  // ---------------- Estate Tax ----------------
  "estate-tax-2026": {
    howToSteps: [
      {
        title: "Enter total estate value",
        body: "Sum of everything you own at death: home equity, retirement accounts, taxable investments, life insurance (if owned by you), business interests, personal property. Use today's fair market value.",
      },
      {
        title: "Check 'Married'",
        body: "Spouses effectively get a combined $30M exemption in 2026 via portability. Un-used first-spouse exemption transfers to the surviving spouse's estate if the right election is filed.",
      },
      {
        title: "See taxable estate",
        body: "Your estate value minus the $15M (single) / $30M (married) exemption. Amounts above are taxed at the 40% federal estate tax rate.",
      },
      {
        title: "Consider gifting strategies",
        body: "The exemption applies to lifetime gifts AND estate transfers combined. Annual exclusion gifts ($18,000/person/year in 2024, indexed) don't count against the exemption.",
      },
    ],
    takeaways: [
      "**OBBBA made the doubled exemption permanent** — the 'use it or lose it' 2026 sunset cliff that drove massive 2024-2025 pre-sunset gifting is gone.",
      "Only estates above **$15M single / $30M married** owe any federal estate tax. For 2026 that's roughly the top 0.1% of households — rarer than winning a scratch-off.",
      "The 40% rate applies only to the portion ABOVE the exemption. A $16M estate (single) owes 40% × $1M = $400k — not 40% of the full $16M.",
      "**State estate taxes are separate** — 12 states + DC have their own estate tax, many with MUCH lower exemptions ($1-7M). Check your state if estate is above $5M.",
    ],
    nextSteps: [
      "If net worth is under $10M, you likely don't need aggressive estate planning — focus on a good will, durable POA, and healthcare directive.",
      "Above $10M (single) or $20M (married), start conversations with an estate attorney about trusts (GRAT, SLAT, dynasty trust) — less urgent now but still valuable.",
      "Don't forget state estate tax exposure — a [[Massachusetts|state:massachusetts]] or [[Oregon|state:oregon]] resident with a $3M estate may still owe significant state tax even with zero federal.",
    ],
    glossary: [
      {
        term: "Estate exemption",
        definition:
          "The dollar amount each person can transfer tax-free at death (or during life via gifts). $15M per person in 2026, inflation-adjusted annually under OBBBA.",
      },
      {
        term: "Portability",
        definition:
          "Allows a surviving spouse to use their deceased spouse's unused exemption, effectively doubling the couple's combined exemption to $30M (2026). Requires filing Form 706.",
      },
    ],
  },

  // ---------------- No Tax on Tips ----------------
  "no-tax-on-tips": {
    howToSteps: [
      {
        title: "Enter annual tip income",
        body: "Total reported tips for the year from your W-2 box 7 plus any cash tips you tracked separately. Auto-gratuity and service charges do NOT count as tips under OBBBA — they're regular wages.",
      },
      {
        title: "Select filing status",
        body: "Single filers get up to $25k deducted; MFJ filers also get $25k (per return, not per spouse). Phase-outs start at $150k MAGI single / $300k MFJ.",
      },
      {
        title: "Enter MAGI",
        body: "Modified Adjusted Gross Income — roughly your AGI. If your MAGI is under the phase-out threshold, you get the full deduction. Above, it reduces at $100 per $1,000 of excess income.",
      },
      {
        title: "See your deduction",
        body: "The calculator shows your eligible deduction (capped at $25k, phased out above the threshold) and the federal tax savings at your marginal bracket rate.",
      },
    ],
    takeaways: [
      "**'No Tax on Tips' is a deduction, not an exclusion** — you still report tips on your W-2, but OBBBA creates an above-the-line deduction up to $25,000 for 2025-2028 only.",
      "Only **qualifying occupations** are eligible — the Treasury Department publishes a list (restaurants, bars, salons, hospitality, ride-share, delivery). Check the final list before assuming you qualify.",
      "It's an **above-the-line deduction**, meaning it reduces AGI, which can unlock other benefits (higher EITC, lower student-loan payments under IBR plans, qualifying for saver's credit, etc.).",
      "**Tips still count for FICA** (Social Security + Medicare). The 'no tax' refers only to federal income tax — payroll tax on tips continues as before.",
    ],
    nextSteps: [
      "Verify your W-2 correctly reports tips in box 7 — mis-reported tips can disqualify you from the deduction.",
      "If you're close to the $150k/$300k phase-out, consider pre-tax 401(k) contributions to preserve the full deduction.",
      "Because this deduction sunsets after 2028, think of 2025-2028 as a 'make hay while the sun shines' window for tipped workers — maximize retirement contributions and Roth conversions while your effective rate is lower.",
      "If you also work overtime shifts, pair this with the [[Overtime Deduction|no-tax-on-overtime]] calculator — they stack.",
    ],
    glossary: [C.magi, C.marginal_rate, C.agi],
  },

  // ---------------- No Tax on Overtime ----------------
  "no-tax-on-overtime": {
    howToSteps: [
      {
        title: "Enter annual overtime earnings",
        body: "Just the overtime PREMIUM portion — the extra 0.5× above your regular wage on FLSA-defined overtime hours. Your pay stub should show 'overtime premium' or similar. If unsure, take total overtime pay × 1/3.",
      },
      {
        title: "Confirm non-exempt FLSA status",
        body: "Only non-exempt employees (paid hourly or eligible for overtime) qualify. Salaried exempt employees (most professional/managerial roles) don't qualify regardless of hours worked.",
      },
      {
        title: "Set filing status and MAGI",
        body: "Single cap: $12,500 deduction. MFJ cap: $25,000. Phase-out starts at $150k single / $300k MFJ MAGI.",
      },
      {
        title: "See the deduction",
        body: "Calculator caps your overtime premium at the filing-status limit and applies the phase-out. Result shows the federal tax savings at your marginal rate.",
      },
    ],
    takeaways: [
      "Only the **premium portion** of overtime qualifies (the 0.5× on top of base pay) — not the full time-and-a-half payment. $10,000 in overtime pay typically = ~$3,333 in deductible premium.",
      "This is a **2025-2028 temporary deduction** — if you're regularly hitting $25k+ in overtime, now is the time to capture the tax benefit.",
      "The deduction is **above-the-line**, so it reduces AGI and MAGI, potentially unlocking eligibility for other means-tested benefits (EITC, saver's credit, Roth IRA contributions).",
    ],
    nextSteps: [
      "Ask your payroll dept for a year-end report breaking out regular wages vs overtime premium — makes filing much cleaner.",
      "Double-check your exempt/non-exempt classification with HR if you're a salaried hourly worker; misclassification is common.",
      "If you're eligible for both Tips AND Overtime deductions, run both — [[Tips Deduction|no-tax-on-tips]] and [[Overtime Deduction|no-tax-on-overtime]] — the limits are separate and can be stacked.",
    ],
    glossary: [
      C.magi,
      C.agi,
      {
        term: "FLSA non-exempt",
        definition:
          "Fair Labor Standards Act classification requiring overtime pay at 1.5× regular rate for hours over 40/week. Only these workers qualify for OBBBA's overtime deduction.",
      },
    ],
  },

  // ---------------- Senior Deduction ----------------
  "senior-deduction-2026": {
    howToSteps: [
      {
        title: "Enter your age",
        body: "You must be 65 or older by December 31 of the tax year to qualify. If you turn 65 in 2026, you get the full deduction for tax year 2026.",
      },
      {
        title: "Add spouse age (if applicable)",
        body: "If MFJ and BOTH spouses are 65+, each gets a $6,000 deduction — total $12,000 for the household. One spouse under 65 = just $6,000.",
      },
      {
        title: "Pick filing status and MAGI",
        body: "Phase-out starts at $75k MAGI (single) / $150k (MFJ). Reduces at roughly 6% of MAGI excess. Fully phased out above ~$175k single / $250k MFJ.",
      },
      {
        title: "See deduction + savings",
        body: "Calculator shows your eligible deduction (after phase-out) and the federal tax savings at your marginal rate.",
      },
    ],
    takeaways: [
      "**This is a BRAND NEW deduction under OBBBA** — it's ON TOP of the existing age-65 additional standard deduction (~$1,600-$2,000), not a replacement.",
      "**Fully phased out** for singles with MAGI above ~$175k and MFJ above ~$250k. Most middle-income retirees get the full amount.",
      "Available **2025-2028 only** — temporary provision. A married couple both 65+ can deduct up to $12k/year × 4 years = $48,000 over the window.",
      "Unlike tips/overtime, this deduction is **available regardless of whether you're still working or fully retired**. Earned, investment, or pension income all count toward MAGI.",
    ],
    nextSteps: [
      "Retirees living on Social Security + modest withdrawals are likely well under the phase-out — no planning needed, just claim the deduction.",
      "If taking RMDs is pushing MAGI near $75k/$150k, consider Qualified Charitable Distributions (QCDs) from IRAs, which count toward RMDs but not MAGI.",
      "Couples where one spouse is 65+ and the other is younger should consider delaying MFJ-level expenses until both cross 65 — doubles the deduction.",
      "Also double-check your total deductions with the [[Standard Deduction|standard-deduction-2026]] calculator — this $6k is ON TOP, not a replacement.",
    ],
    glossary: [C.magi, C.filing_status, C.standard_deduction],
  },

  // ---------------- Auto Loan Interest ----------------
  "auto-loan-interest-2026": {
    howToSteps: [
      {
        title: "Enter annual auto loan interest",
        body: "The interest portion of your payments for the year. Check your lender's year-end statement (Form 1098-E equivalent) or sum the interest column from your amortization schedule.",
      },
      {
        title: "Confirm vehicle qualifies",
        body: "Must be a NEW vehicle (not used), final assembly in the United States, for personal use (not business), model year 2025 or later, loan originated after Jan 1, 2025.",
      },
      {
        title: "Pick filing status + MAGI",
        body: "Cap: $10,000/year of deductible interest. Phase-out starts at $100k MAGI (single) / $200k (MFJ). Reduces $200 per $1,000 of excess MAGI.",
      },
      {
        title: "See the deduction",
        body: "Calculator caps interest at $10k, applies phase-out, and shows the federal tax savings at your marginal rate.",
      },
    ],
    takeaways: [
      "**US-assembled requirement is strict** — check the 17-digit VIN: position 1 should be '1', '4', or '5' for US-assembled. Canadian/Mexican-built vehicles (VIN starting with 2 or 3) don't qualify even if the brand is American.",
      "The deduction is **above-the-line**, available whether you itemize or take the standard deduction. Rare and valuable combination.",
      "**Used vehicles don't qualify** — must be a new vehicle purchased after OBBBA's effective date. Lease interest also doesn't qualify (leases aren't loans).",
      "Available **2025-2028 only**. For someone taking a 5-year loan in 2025, years 4-5 of interest won't qualify — front-loaded benefit.",
    ],
    nextSteps: [
      "Before buying a new car, confirm US final assembly via the manufacturer's dealer sheet or the Department of Energy's VIN decoder (fueleconomy.gov).",
      "Consider a shorter loan term — higher monthly payments but more interest deductible during the 2025-2028 window.",
      "Don't let the tax deduction drive the buying decision — at a 22% marginal rate, deducting $10k of interest saves $2,200. Still less than depreciation on an unnecessary new car.",
    ],
    glossary: [C.magi, C.marginal_rate],
  },
};

/** Returns the copy object for a given calculator slug, or a minimal fallback. */
export const getCalcCopy = (slug) => CALC_COPY[slug] || {};
